import { logger } from '../utils/logger';
import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBranch } from '../contexts/BranchContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { getPendingApprovals } from '../services/api';
import PendingApprovalsModal from './PendingApprovalsModal';
import RankingModal from './RankingModal';
import { io } from 'socket.io-client';
import './Navbar.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:5005');

const Navbar = () => {
  const { user, logout } = useAuth();
  const { selectedBranch, setSelectedBranch, availableBranches } = useBranch();
  const navigate = useNavigate();
  const location = useLocation();
  const [isBranchDropdownOpen, setIsBranchDropdownOpen] = React.useState(false);
  const [isPendingApprovalsOpen, setIsPendingApprovalsOpen] = React.useState(false);
  const [isRankingModalOpen, setIsRankingModalOpen] = React.useState(false);
  const [pendingApprovalsCount, setPendingApprovalsCount] = React.useState(0);

  const isApprover = user?.role === 'admin' || user?.role === 'osgb_admin' || user?.role === 'coordinator' || user?.role === 'auditor';

  React.useEffect(() => {
    if (!isApprover) return;

    const fetchCount = async () => {
      try {
        const data = await getPendingApprovals();
        if (Array.isArray(data)) {
          setPendingApprovalsCount(data.length);
        }
      } catch (e) {
        // silent fail
      }
    };

    fetchCount();

    const interval = setInterval(fetchCount, 10000);
    window.addEventListener('workplaceUpdated', fetchCount);
    window.addEventListener('focus', fetchCount);

    const socket = io(API_BASE_URL);
    socket.on('documentUploaded', () => fetchCount());
    socket.on('documentApproved', () => fetchCount());
    socket.on('documentRejected', () => fetchCount());

    return () => {
      clearInterval(interval);
      window.removeEventListener('workplaceUpdated', fetchCount);
      window.removeEventListener('focus', fetchCount);
      socket.disconnect();
    };
  }, [isApprover, selectedBranch]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Function to determine if a link is active
  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    if (path === '/dashboard') return location.pathname === '/dashboard' || location.pathname === '/workplaces';
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  const isSystemAdmin = user?.role === 'admin' && user?.username === 'admin';
  const { daysRemaining, isActive: isSubActive, loading: subLoading } = useSubscription();

  // Determine badge color
  const getBadgeColor = () => {
    if (daysRemaining === null) return '#ccc';
    if (daysRemaining > 30) return '#2ed573'; // Green
    if (daysRemaining > 7) return '#ffa502'; // Orange
    return '#ff4757'; // Red
  };

  const formatUserName = (fullName: string) => {
    if (!fullName) return '';
    const parts = fullName.trim().split(/\s+/);
    let displayName = fullName;
    
    if (parts.length >= 2) {
      const lastName = parts.pop();
      const firstName = parts.join(' ');
      displayName = `${firstName} ${lastName ? lastName.charAt(0) + '.' : ''}`;
    }

    // Role-based organization name prefix for HR, Accounting, Coordinator, and Auditor
    if ((user?.role === 'hr' || user?.role === 'accounting' || user?.role === 'coordinator' || user?.role === 'auditor') && user.Organization?.name) {
      displayName = `${user.Organization.name} / ${displayName}`;
    }

    return (
      <Link to="/profile" className="navbar-profile-link" title={displayName}>
        {displayName}
      </Link>
    );
  };

  // Debug logging for subscription badge
  React.useEffect(() => {
    if (user?.role === 'admin' && user?.organizationId) {
      logger.debug('[Navbar] Subscription Badge Debug:', {
        daysRemaining,
        isSubActive,
        subLoading,
        shouldShowBadge: daysRemaining !== null,
        user: user.fullName
      });
    }
  }, [daysRemaining, isSubActive, subLoading, user]);

  return (
    <nav className={`navbar ${isSystemAdmin ? 'admin-navbar' : ''}`}>
      <div className="navbar-brand">
        <Link to={user?.role === 'client' ? "/client/dashboard" : (user?.role === 'hr' || user?.role === 'accounting' || user?.role === 'arsiv' || user?.role === 'coordinator' || user?.role === 'auditor') ? "/dashboard" : "/"} className="navbar-brand-title">
          OSGB YÖNETİM SİSTEMİ
        </Link>
        <div className="navbar-brand-actions">
          {isApprover && (
            <button
              type="button"
              className="navbar-pending-approvals-btn"
              onClick={() => setIsPendingApprovalsOpen(true)}
              title={user?.role === 'auditor' ? "Koordinatör onayından geçmiş son denetim evraklarını inceleyin" : "Onay bekleyen saha evraklarını inceleyin"}
              style={user?.role === 'auditor' ? { 
                background: 'linear-gradient(135deg, #ea580c 0%, #c2410c 100%)', 
                border: '1px solid #fdba74',
                boxShadow: '0 2px 8px rgba(234, 88, 12, 0.45)'
              } : undefined}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '3px', lineHeight: '1.1' }}>
                <i className={user?.role === 'auditor' ? "fas fa-stamp" : "fas fa-clipboard-check"} style={{ fontSize: '9px' }}></i>
                <span>{user?.role === 'auditor' ? 'Son Onay' : 'Onay Bekleyen'}</span>
              </div>
              <div
                className={pendingApprovalsCount > 0 ? 'pending-count-blinking' : ''}
                style={{
                  backgroundColor: pendingApprovalsCount > 0 ? '#ffffff' : 'rgba(255,255,255,0.25)',
                  color: pendingApprovalsCount > 0 ? '#d97706' : '#ffffff',
                  borderRadius: '6px',
                  padding: '0px 6px',
                  fontSize: '9px',
                  fontWeight: '800',
                  lineHeight: '12px',
                  marginTop: '1px'
                }}
              >
                {pendingApprovalsCount > 0 ? `${pendingApprovalsCount} Evrak` : '0 Evrak'}
              </div>
            </button>
          )}

          <a
            href="https://wa.me/905072136474"
            target="_blank"
            rel="noopener noreferrer"
            className="navbar-whatsapp-btn"
            title="WhatsApp Teknik Destek: 0507 213 64 74"
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.03)';
              e.currentTarget.style.backgroundColor = '#20bd5a';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.backgroundColor = '#25D366';
            }}
          >
            <i className="fab fa-whatsapp" style={{ fontSize: '12px' }}></i>
            <span>TEKNİK DESTEK</span>
          </a>
        </div>
      </div>

      <ul className="navbar-nav">
        {user?.role === 'client' ? (
          <li className="nav-item">
            <Link to="/client/dashboard" className={`nav-link ${isActive('/client/dashboard') ? 'active' : ''}`}>Müşteri Paneli</Link>
          </li>
        ) : (user?.role === 'hr' || user?.role === 'accounting') ? (
          <>
            <li className="nav-item">
              <Link to="/experts" className={`nav-link ${isActive('/experts') ? 'active' : ''}`}>Uzman</Link>
            </li>
            <li className="nav-item">
              <Link to="/doctors" className={`nav-link ${isActive('/doctors') ? 'active' : ''}`}>Hekim</Link>
            </li>
            <li className="nav-item">
              <Link to="/dsps" className={`nav-link ${isActive('/dsps') ? 'active' : ''}`}>DSP</Link>
            </li>
            <li className="nav-item">
              <Link to="/isg-katip" className={`nav-link ${isActive('/isg-katip') ? 'active' : ''}`}>İSG KATİP</Link>
            </li>
            <li className="nav-item">
              <Link to="/accounting" className={`nav-link ${isActive('/accounting') ? 'active' : ''}`}>MUHASEBE</Link>
            </li>
            <li className="nav-item branch-dropdown-container">
              <button 
                className={`nav-link branch-dropdown-button ${availableBranches.length === 0 ? 'disabled' : ''}`}
                onClick={() => availableBranches.length > 0 && setIsBranchDropdownOpen(!isBranchDropdownOpen)}
                disabled={availableBranches.length === 0}
                style={{ cursor: availableBranches.length === 0 ? 'not-allowed' : 'pointer', opacity: availableBranches.length === 0 ? 0.5 : 1 }}
              >
                {selectedBranch === 'Tüm Şubeler' ? 'Şubeler' : selectedBranch}
                {availableBranches.length > 0 && <i className={`fas fa-chevron-${isBranchDropdownOpen ? 'up' : 'down'}`} style={{ marginLeft: '5px', fontSize: '0.8rem' }}></i>}
              </button>
              
              {isBranchDropdownOpen && availableBranches.length > 0 && (
                <div className="branch-dropdown-menu">
                  <div 
                    className={`branch-dropdown-item ${selectedBranch === 'Tüm Şubeler' ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedBranch('Tüm Şubeler');
                      setIsBranchDropdownOpen(false);
                    }}
                  >
                    Tüm Şubeler
                  </div>
                  {availableBranches.map((branch: string) => (
                    <div 
                      key={branch}
                      className={`branch-dropdown-item ${selectedBranch === branch ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedBranch(branch);
                        setIsBranchDropdownOpen(false);
                      }}
                    >
                      {branch}
                    </div>
                  ))}
                </div>
              )}
            </li>
            <li className="nav-item">
              <a
                href="https://chromewebstore.google.com/detail/i%CC%87sg-destek/kmccocjabhgjidlmimobpeiadlgpaicd"
                target="_blank"
                rel="noopener noreferrer"
                className="nav-link nav-extension-link"
                id="nav-extension-link"
              >
                <i className="fas fa-puzzle-piece" style={{ marginRight: '5px' }}></i>
                EKLENTİ
              </a>
            </li>
          </>
        ) : user?.role === 'arsiv' ? (
          <>
            <li className="nav-item">
              <Link to="/archive" className={`nav-link ${isActive('/archive') ? 'active' : ''}`}>
                <i className="fas fa-archive" style={{ marginRight: '5px' }}></i>
                ARŞİV
              </Link>
            </li>
            <li className="nav-item">
              <Link to="/statistics" className={`nav-link ${isActive('/statistics') ? 'active' : ''}`}>İstatistik</Link>
            </li>
            <li className="nav-item branch-dropdown-container">
              <button 
                className={`nav-link branch-dropdown-button ${availableBranches.length === 0 ? 'disabled' : ''}`}
                onClick={() => availableBranches.length > 0 && setIsBranchDropdownOpen(!isBranchDropdownOpen)}
                disabled={availableBranches.length === 0}
                style={{ cursor: availableBranches.length === 0 ? 'not-allowed' : 'pointer', opacity: availableBranches.length === 0 ? 0.5 : 1 }}
              >
                {selectedBranch === 'Tüm Şubeler' ? 'Şubeler' : selectedBranch}
                {availableBranches.length > 0 && <i className={`fas fa-chevron-${isBranchDropdownOpen ? 'up' : 'down'}`} style={{ marginLeft: '5px', fontSize: '0.8rem' }}></i>}
              </button>
              
              {isBranchDropdownOpen && availableBranches.length > 0 && (
                <div className="branch-dropdown-menu">
                  <div 
                    className={`branch-dropdown-item ${selectedBranch === 'Tüm Şubeler' ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedBranch('Tüm Şubeler');
                      setIsBranchDropdownOpen(false);
                    }}
                  >
                    Tüm Şubeler
                  </div>
                  {availableBranches.map((branch: string) => (
                    <div 
                      key={branch}
                      className={`branch-dropdown-item ${selectedBranch === branch ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedBranch(branch);
                        setIsBranchDropdownOpen(false);
                      }}
                    >
                      {branch}
                    </div>
                  ))}
                </div>
              )}
            </li>
          </>
        ) : (user?.role === 'coordinator' || user?.role === 'auditor') ? (
          <>
            <li className="nav-item">
              <Link to="/" className={`nav-link ${isActive('/') ? 'active' : ''}`}>ANA SAYFA</Link>
            </li>
            <li className="nav-item">
              <Link to="/dashboard" className={`nav-link ${isActive('/dashboard') ? 'active' : ''}`}>İşyerleri</Link>
            </li>
            <li className="nav-item">
              <Link to="/experts" className={`nav-link ${isActive('/experts') ? 'active' : ''}`}>Uzman</Link>
            </li>
            <li className="nav-item">
              <Link to="/doctors" className={`nav-link ${isActive('/doctors') ? 'active' : ''}`}>Hekim</Link>
            </li>
            <li className="nav-item">
              <Link to="/dsps" className={`nav-link ${isActive('/dsps') ? 'active' : ''}`}>DSP</Link>
            </li>
            <li className="nav-item">
              <Link to="/visits" className={`nav-link ${isActive('/visits') ? 'active' : ''}`}>Ziyaretler</Link>
            </li>
            <li className="nav-item">
              <Link to="/statistics" className={`nav-link ${isActive('/statistics') ? 'active' : ''}`}>İstatistik</Link>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className="nav-link ranking-nav-btn"
                onClick={() => setIsRankingModalOpen(true)}
                title="OSGB Başarı Sıralaması"
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontWeight: 600,
                  fontSize: 'inherit',
                  fontFamily: 'inherit',
                  padding: '0.5rem 0.8rem',
                  color: 'inherit'
                }}
              >
                <i className="fas fa-trophy" style={{ color: '#f59e0b', fontSize: '0.9rem' }}></i>
                <span>SIRALAMA</span>
              </button>
            </li>
            <li className="nav-item">
              <Link to="/accounting" className={`nav-link ${isActive('/accounting') ? 'active' : ''}`}>MUHASEBE</Link>
            </li>
            <li className="nav-item">
              <Link to="/archive" className={`nav-link ${isActive('/archive') ? 'active' : ''}`}>
                <i className="fas fa-archive" style={{ marginRight: '5px' }}></i>
                ARŞİV
              </Link>
            </li>
            <li className="nav-item branch-dropdown-container">
              <button 
                className={`nav-link branch-dropdown-button ${availableBranches.length === 0 ? 'disabled' : ''}`}
                onClick={() => availableBranches.length > 0 && setIsBranchDropdownOpen(!isBranchDropdownOpen)}
                disabled={availableBranches.length === 0}
                style={{ cursor: availableBranches.length === 0 ? 'not-allowed' : 'pointer', opacity: availableBranches.length === 0 ? 0.5 : 1 }}
              >
                {selectedBranch === 'Tüm Şubeler' ? 'Şubeler' : selectedBranch}
                {availableBranches.length > 0 && <i className={`fas fa-chevron-${isBranchDropdownOpen ? 'up' : 'down'}`} style={{ marginLeft: '5px', fontSize: '0.8rem' }}></i>}
              </button>
              
              {isBranchDropdownOpen && availableBranches.length > 0 && (
                <div className="branch-dropdown-menu">
                  {availableBranches.map((branch: string) => (
                    <div 
                      key={branch}
                      className={`branch-dropdown-item ${selectedBranch === branch ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedBranch(branch);
                        setIsBranchDropdownOpen(false);
                      }}
                    >
                      {branch}
                    </div>
                  ))}
                </div>
              )}
            </li>
          </>
        ) : (
          <>
            <li className="nav-item">
              <Link to="/" className={`nav-link ${isActive('/') ? 'active' : ''}`}>ANA SAYFA</Link>
            </li>
            <li className="nav-item">
              <Link to="/dashboard" className={`nav-link ${isActive('/dashboard') ? 'active' : ''}`}>İşyerleri</Link>
            </li>
            <li className="nav-item">
              <Link to="/experts" className={`nav-link ${isActive('/experts') ? 'active' : ''}`}>Uzman</Link>
            </li>
            <li className="nav-item">
              <Link to="/doctors" className={`nav-link ${isActive('/doctors') ? 'active' : ''}`}>Hekim</Link>
            </li>
            <li className="nav-item">
              <Link to="/dsps" className={`nav-link ${isActive('/dsps') ? 'active' : ''}`}>DSP</Link>
            </li>
            <li className="nav-item">
              <Link to="/visits" className={`nav-link ${isActive('/visits') ? 'active' : ''}`}>Ziyaretler</Link>
            </li>
            <li className="nav-item">
              <Link to="/statistics" className={`nav-link ${isActive('/statistics') ? 'active' : ''}`}>İstatistik</Link>
            </li>
            <li className="nav-item">
              <Link to="/isg-katip" className={`nav-link ${isActive('/isg-katip') ? 'active' : ''}`}>İSG KATİP</Link>
            </li>
            <li className="nav-item">
              <Link to="/accounting" className={`nav-link ${isActive('/accounting') ? 'active' : ''}`}>MUHASEBE</Link>
            </li>
            <li className="nav-item">
              <Link to="/archive" className={`nav-link ${isActive('/archive') ? 'active' : ''}`}>
                <i className="fas fa-archive" style={{ marginRight: '5px' }}></i>
                ARŞİV
              </Link>
            </li>
            <li className="nav-item">
              <Link to="/settings" className={`nav-link ${isActive('/settings') ? 'active' : ''}`}>Ayarlar</Link>
            </li>
            <li className="nav-item branch-dropdown-container">
              <button 
                className={`nav-link branch-dropdown-button ${availableBranches.length === 0 ? 'disabled' : ''}`}
                onClick={() => availableBranches.length > 0 && setIsBranchDropdownOpen(!isBranchDropdownOpen)}
                disabled={availableBranches.length === 0}
                style={{ cursor: availableBranches.length === 0 ? 'not-allowed' : 'pointer', opacity: availableBranches.length === 0 ? 0.5 : 1 }}
              >
                {selectedBranch === 'Tüm Şubeler' ? 'Şubeler' : selectedBranch}
                {availableBranches.length > 0 && <i className={`fas fa-chevron-${isBranchDropdownOpen ? 'up' : 'down'}`} style={{ marginLeft: '5px', fontSize: '0.8rem' }}></i>}
              </button>
              
              {isBranchDropdownOpen && availableBranches.length > 0 && (
                <div className="branch-dropdown-menu">
                  <div 
                    className={`branch-dropdown-item ${selectedBranch === 'Tüm Şubeler' ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedBranch('Tüm Şubeler');
                      setIsBranchDropdownOpen(false);
                    }}
                  >
                    Tüm Şubeler
                  </div>
                  {availableBranches.map((branch: string) => (
                    <div 
                      key={branch}
                      className={`branch-dropdown-item ${selectedBranch === branch ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedBranch(branch);
                        setIsBranchDropdownOpen(false);
                      }}
                    >
                      {branch}
                    </div>
                  ))}
                </div>
              )}
            </li>
            <li className="nav-item">
              <a
                href="https://chromewebstore.google.com/detail/i%CC%87sg-destek/kmccocjabhgjidlmimobpeiadlgpaicd"
                target="_blank"
                rel="noopener noreferrer"
                className="nav-link nav-extension-link"
                id="nav-extension-link"
              >
                <i className="fas fa-puzzle-piece" style={{ marginRight: '5px' }}></i>
                EKLENTİ
              </a>
            </li>
          </>
        )}
        {user?.role === 'admin' && user?.username === 'admin' && (
          <li className="nav-item">
            <Link to="/admin" className={`nav-link ${isActive('/admin') ? 'active' : ''}`}>Admin Panel</Link>
          </li>
        )}
      </ul>

      <div className="navbar-user">
        {user && (
          <div className="navbar-user-content">
            <div className="navbar-user-row">
              {user.organizationId && daysRemaining !== null && (
                (user.role === 'admin' || user.role === 'osgb_admin') ? (
                  <Link to="/subscription" style={{ textDecoration: 'none' }}>
                    <div
                      className={`subscription-badge ${daysRemaining !== null && daysRemaining <= 7 ? 'blinking' : ''}`}
                      style={{ backgroundColor: getBadgeColor(), cursor: 'pointer' }}
                      title="Abonelik durumunu yönetmek için tıklayın"
                    >
                      {daysRemaining > 36500 ? 'Sınırsız' : (isSubActive ? `Kalan: ${daysRemaining} Gün` : 'Süre Doldu - Yenile')}
                    </div>
                  </Link>
                ) : (user.role === 'coordinator' || user.role === 'auditor') ? (
                  <div
                    className={`subscription-badge ${daysRemaining !== null && daysRemaining <= 7 ? 'blinking' : ''}`}
                    style={{ backgroundColor: getBadgeColor(), cursor: 'default', userSelect: 'none' }}
                    title={`Kalan Abonelik Süresi: ${daysRemaining > 36500 ? 'Sınırsız' : `${daysRemaining} Gün`} (Yalnızca Görüntüleme)`}
                  >
                    {daysRemaining > 36500 ? 'Sınırsız' : (isSubActive ? `Kalan: ${daysRemaining} Gün` : 'Süre Doldu')}
                  </div>
                ) : null
              )}

              <div className="navbar-user-name">
                {formatUserName(user.fullName)}
              </div>

              <button className="logout-button" onClick={handleLogout}>
                Çıkış Yap
              </button>
            </div>
          </div>
        )}
      </div>

      {isApprover && (
        <PendingApprovalsModal
          isOpen={isPendingApprovalsOpen}
          onClose={() => setIsPendingApprovalsOpen(false)}
          onPendingCountChange={setPendingApprovalsCount}
        />
      )}

      <RankingModal
        isOpen={isRankingModalOpen}
        onClose={() => setIsRankingModalOpen(false)}
        isStaffView={true}
      />
    </nav>
  );
};

export default Navbar;
